import type { DemandDto } from '../api/models/DemandDto';
import type { CsvImportConfigDto } from '../api/models/CsvImportConfigDto';

export const DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX = 10;

export type ParsedDemandImportOptions = {
  /** null = all materials allowed */
  allowedMaterialUuids: Set<string> | null;
  orderForecastWeeksAhead: number;
};

export function normalizeDemandQuantity(value: unknown): number {
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || t.toUpperCase() === 'NULL') {
      return 0;
    }
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function parseDemandImportOptions(config: CsvImportConfigDto | null | undefined): ParsedDemandImportOptions {
  const raw = config?.demandImportOptions;
  if (!raw || typeof raw !== 'object') {
    return { allowedMaterialUuids: null, orderForecastWeeksAhead: 0 };
  }
  const o = raw as Record<string, unknown>;
  const arr = o.allowedMaterialUuids;
  let allowedMaterialUuids: Set<string> | null = null;
  if (Array.isArray(arr) && arr.length > 0) {
    allowedMaterialUuids = new Set(arr.map((x) => String(x)).filter(Boolean));
  }
  let weeks = Number(o.orderForecastWeeksAhead ?? 0);
  if (!Number.isFinite(weeks)) {
    weeks = 0;
  }
  weeks = Math.max(0, Math.min(DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX, Math.floor(weeks)));
  return { allowedMaterialUuids, orderForecastWeeksAhead: weeks };
}

export function demandRowKey(d: DemandDto): string {
  const dateStr = d.demandDate?.split('T')[0] || d.demandDate || '';
  return `${d.materialId || ''}|${dateStr}|${d.dueTime || ''}`;
}

export function filterDemandsByAllowedMaterials(
  rows: DemandDto[],
  allowedMaterialUuids: Set<string> | null
): { rows: DemandDto[]; skipped: number } {
  if (!allowedMaterialUuids || allowedMaterialUuids.size === 0) {
    return { rows, skipped: 0 };
  }
  const out: DemandDto[] = [];
  let skipped = 0;
  for (const r of rows) {
    if (r.materialId && allowedMaterialUuids.has(r.materialId)) {
      out.push(r);
    } else {
      skipped += 1;
    }
  }
  return { rows: out, skipped };
}

/** Sum quantity per (materialId, calendar day, dueTime); first row wins for other fields. */
export function aggregateDemandRows(rows: DemandDto[]): DemandDto[] {
  const map = new Map<string, DemandDto>();
  for (const d of rows) {
    const k = demandRowKey(d);
    const q = normalizeDemandQuantity(d.quantity);
    const existing = map.get(k);
    if (!existing) {
      map.set(k, { ...d, quantity: q });
    } else {
      map.set(k, {
        ...existing,
        quantity: normalizeDemandQuantity(existing.quantity) + q
      });
    }
  }
  return Array.from(map.values());
}

/**
 * For each order row, append FORECAST rows for the same weekday at +1…+weeksAhead weeks (calendar +7 days each).
 */
export function buildOrderDerivedForecasts(orders: DemandDto[], weeksAhead: number): DemandDto[] {
  if (weeksAhead <= 0) {
    return [];
  }
  const out: DemandDto[] = [];
  for (const o of orders) {
    const base = o.demandDate?.split('T')[0];
    if (!base || !o.materialId) {
      continue;
    }
    const baseDate = new Date(`${base}T12:00:00`);
    if (Number.isNaN(baseDate.getTime())) {
      continue;
    }
    const qty = normalizeDemandQuantity(o.quantity);
    for (let w = 1; w <= weeksAhead; w++) {
      const target = new Date(baseDate);
      target.setDate(target.getDate() + 7 * w);
      const y = target.getFullYear();
      const m = String(target.getMonth() + 1).padStart(2, '0');
      const day = String(target.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${day}`;
      out.push({
        ...o,
        uuid: undefined,
        demandType: 'FORECAST',
        demandDate: `${dateStr}T00:00:00`,
        quantity: qty
      });
    }
  }
  return out;
}
